<template>
  <div>
    <div v-if="loading" class="text-center">
      <b-spinner variant="primary" label="Loading Tele2 data..."></b-spinner>
      <p class="mt-2">{{ $t('globals.loading') }} Tele2 data...</p>
    </div>
    
    <div v-else-if="error" class="alert alert-danger">
      <strong>Error:</strong> {{ error }}
    </div>
    
    <div v-else-if="tele2Data">
      <!-- Device Info Section -->
      <div v-if="tele2Data.device_info" class="card mb-3">
        <div class="card-header d-flex justify-content-between align-items-center">
          <h5 class="mb-0">{{ $t('globals.device') }} {{ $t('globals.info') }}</h5>
          <button 
            v-if="shouldShowStatusButton"
            @click="updateDeviceStatus"
            :disabled="updatingStatus"
            class="btn btn-sm"
            :class="getButtonClass()"
          >
            <b-spinner v-if="updatingStatus" small class="mr-1"></b-spinner>
            {{ getButtonText() }}
          </button>
        </div>
        <div class="card-body">
          <div class="row">
            <div class="col-md-6">
              <dl class="row">
                <dt class="col-sm-4">Status:</dt>
                <dd class="col-sm-8">
                  <span :class="getStatusBadgeClass()">
                    {{ tele2Data.device_info.status || 'N/A' }}
                  </span>
                </dd>
                
                <dt class="col-sm-4">Rate Plan:</dt>
                <dd class="col-sm-8">{{ tele2Data.device_info.rate_plan || 'N/A' }}</dd>
                
                <dt class="col-sm-4">Communication Plan:</dt>
                <dd class="col-sm-8">{{ tele2Data.device_info.communication_plan || 'N/A' }}</dd>
                
                <dt class="col-sm-4">Customer:</dt>
                <dd class="col-sm-8">{{ tele2Data.device_info.customer || 'N/A' }}</dd>
              </dl>
            </div>
            <div class="col-md-6">
              <dl class="row">
                <dt class="col-sm-4">Date Activated:</dt>
                <dd class="col-sm-8">{{ formatDate(tele2Data.device_info.date_activated) || 'N/A' }}</dd>
                
                <dt class="col-sm-4">Date Added:</dt>
                <dd class="col-sm-8">{{ formatDate(tele2Data.device_info.date_added) || 'N/A' }}</dd>
                
                <dt class="col-sm-4">Date Updated:</dt>
                <dd class="col-sm-8">{{ formatDate(tele2Data.device_info.date_updated) || 'N/A' }}</dd>
                
                <dt class="col-sm-4">Date Shipped:</dt>
                <dd class="col-sm-8">{{ formatDate(tele2Data.device_info.date_shipped) || 'N/A' }}</dd>
              </dl>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Session Data Section -->
      <div v-if="hasSessionData" class="card mb-3">
        <div class="card-header">
          <h5 class="mb-0">Session Data</h5>
        </div>
        <div class="card-body">
          <div class="row">
            <div class="col-md-6">
              <dl class="row">
                <dt class="col-sm-4">Session Started:</dt>
                <dd class="col-sm-8">{{ formatSessionDate(sessionStartTime) || 'N/A' }}</dd>
                
                <dt class="col-sm-4">Session Ended:</dt>
                <dd class="col-sm-8">{{ formatSessionDate(sessionEndTime) || 'N/A' }}</dd>
                
                <template v-if="sessionDuration">
                  <dt class="col-sm-4">Duration:</dt>
                  <dd class="col-sm-8">{{ sessionDuration }}</dd>
                </template>
              </dl>
            </div>
            <div class="col-md-6" v-if="hasRawSessionData">
              <dl class="row">
                <dt class="col-sm-4">IP Address:</dt>
                <dd class="col-sm-8">{{ sessionRawData.ipAddress || 'N/A' }}</dd>
                
                <dt class="col-sm-4">APN:</dt>
                <dd class="col-sm-8">{{ sessionRawData.apn || 'N/A' }}</dd>
                
                <dt class="col-sm-4">ICCID:</dt>
                <dd class="col-sm-8">{{ sessionRawData.iccid || 'N/A' }}</dd>
              </dl>
            </div>
          </div>
        </div>
      </div>
      
    </div>
    
    <div v-else class="text-center text-muted">
      <p>No Tele2 data available</p>
    </div>
  </div>
</template>

<script>
import { mapActions } from "vuex";

export default {
  name: "DeviceTele2",
  props: {
    device: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      loading: false,
      error: null,
      tele2Data: null,
      updatingStatus: false
    };
  },
  computed: {
    currentStatus() {
      return this.tele2Data?.device_info?.status;
    },
    shouldShowStatusButton() {
      const allowedStatuses = ['ACTIVATED', 'ACTIVATION_READY', 'DEACTIVATED', 'TEST_READY'];
      return allowedStatuses.includes(this.currentStatus);
    },
    hasSessionData() {
      return this.sessionStartTime || this.sessionEndTime || this.tele2Data?.session_data;
    },
    sessionStartTime() {
      // Handle different possible data structures
      return this.tele2Data?.session_started || 
             this.tele2Data?.session_data?.session_started ||
             this.tele2Data?.raw_data?.dateSessionStarted;
    },
    sessionEndTime() {
      // Handle different possible data structures
      return this.tele2Data?.session_ended || 
             this.tele2Data?.session_data?.session_ended ||
             this.tele2Data?.raw_data?.dateSessionEnded;
    },
    sessionRawData() {
      return this.tele2Data?.raw_data || {};
    },
    hasRawSessionData() {
      const rawData = this.sessionRawData;
      return rawData && (rawData.ipAddress || rawData.apn || rawData.iccid);
    },
    sessionDuration() {
      if (this.sessionStartTime && this.sessionEndTime) {
        const start = new Date(this.sessionStartTime);
        const end = new Date(this.sessionEndTime);
        const durationMs = end - start;
        
        if (durationMs > 0) {
          const minutes = Math.floor(durationMs / (1000 * 60));
          const seconds = Math.floor((durationMs % (1000 * 60)) / 1000);
          
          if (minutes > 0) {
            return `${minutes}m ${seconds}s`;
          } else {
            return `${seconds}s`;
          }
        }
      }
      return null;
    }
  },
  methods: {
    ...mapActions("axios_mod", ["simpleFetch"]),
    
    async fetchTele2Data() {
      // Use actual device ICCID
      const iccid = this.device.iccid;
      
      console.log('Device object:', this.device);
      console.log('ICCID value:', iccid);
      console.log('ICCID type:', typeof iccid);
      
      if (!iccid) {
        this.error = "ICCID is missing. Tele2 data not available.";
        return;
      }
      
      const data = {
        query: `
          query {
            Tele2DeviceData(iccid: "${iccid}")
          }
        `
      };
      
      this.loading = true;
      this.error = null;
      
      try {
        const res = await this.simpleFetch({ data, this_ref: this });
        if (res && res.Tele2DeviceData) {
          const parsedData = JSON.parse(res.Tele2DeviceData);
          if (parsedData.error) {
            this.error = parsedData.error;
          } else {
            this.tele2Data = parsedData;
          }
        }
      } catch (error) {
        this.error = "Failed to fetch Tele2 data: " + error.message;
      } finally {
        this.loading = false;
      }
    },
    
    formatDate(dateString) {
      if (!dateString) return null;
      return new Date(dateString).toLocaleString();
    },
    
    formatSessionDate(dateString) {
      if (!dateString) return null;
      try {
        const date = new Date(dateString);
        // Format with more precision for session times
        return date.toLocaleString('en-US', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false
        });
      } catch (error) {
        console.error('Error formatting session date:', error);
        return dateString;
      }
    },
    
    getButtonText() {
      if (['ACTIVATED', 'ACTIVATION_READY', 'TEST_READY'].includes(this.currentStatus)) {
        return 'Deactivate';
      } else if (this.currentStatus === 'DEACTIVATED') {
        return 'Activate';
      }
      return '';
    },
    
    getButtonClass() {
      if (['ACTIVATED', 'ACTIVATION_READY', 'TEST_READY'].includes(this.currentStatus)) {
        return 'btn-danger';
      } else if (this.currentStatus === 'DEACTIVATED') {
        return 'btn-success';
      }
      return 'btn-secondary';
    },
    
    getStatusBadgeClass() {
      switch (this.currentStatus) {
        case 'ACTIVATED':
          return 'badge badge-success';
        case 'ACTIVATION_READY':
          return 'badge badge-warning';
        case 'DEACTIVATED':
          return 'badge badge-danger';
        case 'TEST_READY':
          return 'badge badge-info';
        case 'INVENTORY':
          return 'badge badge-secondary';
        case 'PURGED':
          return 'badge badge-dark';
        case 'REPLACED':
          return 'badge badge-warning';
        case 'RETIRED':
          return 'badge badge-secondary';
        default:
          return 'badge badge-light';
      }
    },
    
    async updateDeviceStatus() {
      const iccid = this.device.iccid;
      
      if (!iccid) {
        this.$bvToast.toast('ICCID is missing. Cannot update device status.', {
          title: 'Error',
          variant: 'danger',
          solid: true
        });
        return;
      }
      
      let newStatus;
      
      if (['ACTIVATED', 'ACTIVATION_READY', 'TEST_READY'].includes(this.currentStatus)) {
        newStatus = 'DEACTIVATED';
      } else if (this.currentStatus === 'DEACTIVATED') {
        newStatus = 'ACTIVATED';
      }
      
      if (!newStatus) return;
      
      const data = {
        query: `
          query {
            Tele2UpdateDeviceStatus(iccid: "${iccid}", status: "${newStatus}")
          }
        `
      };
      
      this.updatingStatus = true;
      
      try {
        const res = await this.simpleFetch({ data, this_ref: this });
        if (res && res.Tele2UpdateDeviceStatus) {
          const result = JSON.parse(res.Tele2UpdateDeviceStatus);
          if (result.error) {
            throw new Error(result.error);
          } else {
            this.$bvToast.toast(`Device status updated to ${newStatus}`, {
              title: 'Success',
              variant: 'success',
              solid: true
            });
            
            // Refresh the data
            await this.fetchTele2Data();
          }
        }
      } catch (error) {
        this.$bvToast.toast('Failed to update device status: ' + error.message, {
          title: 'Error',
          variant: 'danger',
          solid: true
        });
      } finally {
        this.updatingStatus = false;
      }
    }
  },
  
  mounted() {
    // Only fetch when component is mounted (lazy loading)
    // But wait a bit to ensure device data is loaded
    this.$nextTick(() => {
      if (this.device && this.device.imei) {
        this.fetchTele2Data();
      } else {
        // Wait for device data to load
        setTimeout(() => {
          this.fetchTele2Data();
        }, 500);
      }
    });
  },
  
  watch: {
    device: {
      handler(newDevice) {
        if (newDevice && newDevice.imei && !this.tele2Data && !this.loading) {
          this.fetchTele2Data();
        }
      },
      deep: true
    }
  }
};
</script>

<style scoped>
.card {
  border: 1px solid #dee2e6;
}

.card-header {
  background-color: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
}

pre {
  max-height: 400px;
  overflow-y: auto;
  white-space: pre-wrap;
  word-wrap: break-word;
}

.btn-link {
  text-decoration: none;
  color: #495057;
}

.btn-link:hover {
  text-decoration: none;
  color: #007bff;
}
</style>